import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { ContactsDetails } from 'widgets/ContactsDetails'
import { contactsDetailsGlobalStore } from 'widgets/ContactsDetails/store'
import { useContactsPageContext } from 'pages/contacts/context/contactsPageContext'
import { ContactsPageTools } from 'pages/contacts/ui/ContactsPageTools/ContactsPageTools'
import { ContactsRoutes } from '../route/type'
import { ContactsContent } from './ContactsContent/ContactsContent'
import styles from './styles.module.scss'

const parseContactId = (value?: string): number | null => {
  if (!value) return null

  const parsed = Number(value)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

export const ContactsPage = observer(() => {
  const contactsPageStore = useContactsPageContext()
  const { userPermissionsStore } = useEntitiesContext()
  const { contactsTableStore, contactsDetailsStore, contactsFilterStore, importContactsStore } =
    contactsPageStore
  const { tableStore } = contactsTableStore
  const { isImportAllowed, getStatusImportContact } = importContactsStore
  const { id } = useParams()
  const navigate = useNavigate()
  const activeContactId = contactsTableStore.activeContact?.id ?? null
  const prevActiveIdRef = useRef<number | null>(null)

  useEffect(() => {
    const disposeContactsFilterStore = contactsFilterStore.watchChanges()
    contactsTableStore.initReactions()
    contactsFilterStore.initPreselectFilters()

    if (!contactsFilterStore.segmentBox.segment) {
      contactsTableStore.loadContacts()
    }

    contactsDetailsGlobalStore.handleSetVariant('contacts')
    contactsDetailsGlobalStore.handleChangeCollapse(true)
    if (userPermissionsStore.getItem('can_create_contacts')) {
      !isImportAllowed && getStatusImportContact()
      importContactsStore.contactsImportModuleStore.loadImportHistory()
    }

    contactsPageStore.init()

    return () => {
      disposeContactsFilterStore()
      contactsTableStore.dispose()
      contactsTableStore.resetNewContactMode()
      tableStore.resetSelected()
      contactsFilterStore.clearSegment()
      contactsTableStore.resetSorting()
      contactsPageStore.reset()
    }
  }, [])

  useEffect(() => {
    const parsed = parseContactId(id)

    if (parsed === null) {
      if (contactsTableStore.activeContact) {
        contactsTableStore.setActiveContact(null)
      }

      return
    }

    if (parsed === contactsTableStore.activeContact?.id) return

    let cancelled = false

    contactsPageStore.openContactById(parsed).then((opened) => {
      if (cancelled) return

      if (!opened) {
        navigate(`/${ContactsRoutes.root}/${ContactsRoutes.all}`, { replace: true })
      }
    })

    return () => {
      cancelled = true
    }
  }, [id])

  useEffect(() => {
    const parsed = parseContactId(id)
    const prev = prevActiveIdRef.current
    prevActiveIdRef.current = activeContactId

    if (activeContactId !== null) {
      if (activeContactId !== parsed) {
        navigate(`/${ContactsRoutes.root}/${activeContactId}`)
      }

      return
    }

    if (prev !== null && parsed !== null) {
      navigate(`/${ContactsRoutes.root}/${ContactsRoutes.all}`, { replace: true })
    }
  }, [activeContactId])

  return (
    <div className={styles.container}>
      <ContactsContent />
      {contactsDetailsStore ? <ContactsDetails store={contactsDetailsStore} /> : null}
      <ContactsPageTools />
    </div>
  )
})
